/**
 * 邮百工GUI渲染逻辑统一 - 核心Mermaid渲染组件
 * 负责渲染Mermaid图表，处理错误情况和降级显示
 */

import React, { useRef, useState, useEffect } from 'react';
import { preprocessMermaidChart } from './ContentUtils';

// 缓存已渲染的图表，避免重复渲染
const mermaidCache = new Map();

/**
 * 核心Mermaid渲染组件
 * @param {Object} props - 组件属性
 * @param {string} props.chart - 图表内容
 * @param {string} props.className - 额外的CSS类名
 * @param {string} props.theme - Mermaid主题 (默认: 'neutral')
 * @returns {JSX.Element} - 渲染后的Mermaid图表组件
 */
const CoreMermaid = ({ chart, className = '', theme = 'dark' }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isDarkTheme = theme === 'dark';

  const applyNodeTextContrast = (svgElement) => {
    if (!svgElement) return;
    const parseColor = (value) => {
      if (!value) return null;
      const color = value.trim();
      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 3) {
          return {
            r: parseInt(hex[0] + hex[0], 16),
            g: parseInt(hex[1] + hex[1], 16),
            b: parseInt(hex[2] + hex[2], 16),
          };
        }
        if (hex.length === 6) {
          return {
            r: parseInt(hex.slice(0, 2), 16),
            g: parseInt(hex.slice(2, 4), 16),
            b: parseInt(hex.slice(4, 6), 16),
          };
        }
      }
      const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (rgbMatch) {
        return {
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
        };
      }
      return null;
    };
    const getLuminance = ({ r, g, b }) => (0.299 * r + 0.587 * g + 0.114 * b);

    svgElement.querySelectorAll('g.node').forEach((node) => {
      const shape = node.querySelector('rect, polygon, circle, path');
      if (!shape) return;
      const fill = shape.getAttribute('fill') || shape.style.fill;
      const rgb = parseColor(fill);
      if (!rgb) return;
      const luminance = getLuminance(rgb);
      const textColor = luminance > 170 ? '#111827' : '#e5e7eb';
      node.querySelectorAll('text').forEach((textEl) => {
        textEl.setAttribute('fill', textColor);
        textEl.style.fill = textColor;
      });
      node.querySelectorAll('foreignObject *').forEach((foEl) => {
        foEl.style.color = textColor;
      });
    });
  };
  
  useEffect(() => {
    if (!chart || !containerRef.current) return;
    
    const renderMermaid = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 预处理图表内容
        const processedChart = preprocessMermaidChart(chart);
        const chartId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        
        // 计算缓存键（加入主题版本号，避免缓存白底SVG）
        const themeCacheKey = isDarkTheme ? 'dark-v2' : 'light-v2';
        const cacheKey = `${processedChart}-${themeCacheKey}`;
        
        // 检查缓存
        if (mermaidCache.has(cacheKey)) {
          containerRef.current.innerHTML = mermaidCache.get(cacheKey);
          setIsLoading(false);
          return;
        }
        
        // 动态导入Mermaid库
        const mermaid = (await import('mermaid')).default;
        
        const isDarkTheme = theme === 'dark';
        const themeVariables = isDarkTheme
          ? {
              background: '#1e1e1e',
              primaryColor: '#2b2b2b',
              secondaryColor: '#2a2f3a',
              tertiaryColor: '#3a2f2f',
              primaryTextColor: '#e5e7eb',
              secondaryTextColor: '#cbd5f5',
              tertiaryTextColor: '#a1a1aa',
              primaryBorderColor: '#3a3a3a',
              secondaryBorderColor: '#3f3f46',
              tertiaryBorderColor: '#4b5563',
              lineColor: '#9ca3af',
              textColor: '#e5e7eb',
              nodeBorder: '#4b5563',
              nodeBkg: '#2b2f36',
              clusterBkg: '#1f2937',
              clusterBorder: '#374151',
              defaultLinkColor: '#9ca3af',
              titleColor: '#e5e7eb',
              edgeLabelBackground: '#111827',
              actorBorder: '#4b5563',
              actorBkg: '#1f2937',
              actorTextColor: '#e5e7eb',
              signalColor: '#e5e7eb',
              signalTextColor: '#e5e7eb',
              labelBoxBkgColor: '#111827',
              labelBoxBorderColor: '#4b5563',
              labelTextColor: '#e5e7eb',
              loopTextColor: '#e5e7eb',
              noteBkgColor: '#3b2f2f',
              noteTextColor: '#f3f4f6',
              noteBorderColor: '#6b7280',
              altBackground: '#1f2937',
              fontSize: '15px',
            }
          : {
              background: '#ffffff',
              primaryColor: '#f0f7ff',
              secondaryColor: '#f5f5f5',
              tertiaryColor: '#fff9e6',
              primaryTextColor: '#333333',
              secondaryTextColor: '#666666',
              tertiaryTextColor: '#888888',
              primaryBorderColor: '#d1e4f9',
              secondaryBorderColor: '#e0e0e0',
              tertiaryBorderColor: '#ffe082',
              lineColor: '#999999',
              textColor: '#333333',
              nodeBorder: '#cce0ff',
              nodeBkg: '#f0f8ff',
              clusterBkg: '#fafafa',
              clusterBorder: '#e0e0e0',
              defaultLinkColor: '#999999',
              titleColor: '#333333',
              edgeLabelBackground: '#ffffff',
              actorBorder: '#d1e4f9',
              actorBkg: '#f0f7ff',
              actorTextColor: '#333333',
              signalColor: '#333333',
              signalTextColor: '#333333',
              labelBoxBkgColor: '#ffffff',
              labelBoxBorderColor: '#d1e4f9',
              labelTextColor: '#333333',
              loopTextColor: '#333333',
              noteBkgColor: '#fff9e6',
              noteTextColor: '#333333',
              noteBorderColor: '#ffe082',
              altBackground: '#fafafa',
              fontSize: '15px',
            };

        // 初始化Mermaid配置
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          securityLevel: 'loose', // 允许点击事件和回调
          logLevel: 'error',
          suppressErrorRendering: true, // 禁用Mermaid自带的炸弹错误显示
          fontFamily: 'var(--font-family)', // 使用全局字体变量
          themeVariables,
        });
        
        // 渲染图表
        const { svg } = await mermaid.render(chartId, processedChart);
        
        // 缓存渲染结果
        mermaidCache.set(cacheKey, svg);
        
        // 更新DOM
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          
          // 处理SVG元素，使其响应式
          const svgElement = containerRef.current.querySelector('svg');
          if (svgElement) {
            svgElement.style.maxWidth = '100%';
            svgElement.style.height = 'auto';
            applyNodeTextContrast(svgElement);
            
            // 添加无障碍标题和描述
            const title = document.createElement('title');
            title.id = `chart-title-${chartId}`;
            title.textContent = '图表';
            
            const desc = document.createElement('desc');
            desc.id = `chart-desc-${chartId}`;
            desc.textContent = '邮百工渲染的Mermaid图表';
            
            svgElement.setAttribute('aria-labelledby', `chart-title-${chartId}`);
            svgElement.setAttribute('aria-describedby', `chart-desc-${chartId}`);
            
            svgElement.prepend(title);
            svgElement.prepend(desc);
          }
        }
      } catch (err) {
        console.error('Mermaid渲染错误:', err);
        setError('图表渲染失败');
        
        // 降级为显示简洁的错误信息，而不是原始代码
        if (containerRef.current) {
          containerRef.current.innerHTML = `<div class="simple-error-message" style="color: #888888;">图表渲染失败</div>`;
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    renderMermaid();
    
    // 组件卸载时清理
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [chart, theme]);
  
  return (
    <div
      className={`core-mermaid ${className} ${error ? 'has-error' : ''}`}
      style={isDarkTheme ? { backgroundColor: '#1f2937', borderRadius: '6px', padding: '8px' } : undefined}
      data-mermaid-source={chart || ''}
    >
      {isLoading && <div className="mermaid-loading">加载图表中...</div>}
      {error && <div className="mermaid-error" style={{color: '#888888'}}>图表渲染失败</div>}
      <div ref={containerRef} className="mermaid-container"></div>
    </div>
  );
};

export default CoreMermaid; 